import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Receipt, DollarSign, Plus, Trash2, Search, Eye, Upload, CheckCircle, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import InvoiceViewer from '@/components/InvoiceViewer';
import { formatCurrency, fmtNum } from '@/utils/currency';
import CurrencySymbol from '@/components/CurrencySymbol';
import { SearchFilter } from '@/components/SearchFilter';

export default function Invoices() {
  const { token } = useContext(AuthContext);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    items: [],
    discount: 0,
    tax: 10,
    notes: '',
    customer_notes: '',
    due_date: ''
  });
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  
  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1); // 1: Currency, 2: Bank, 3: Slip
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    currency: '',
    bank_id: '',
    payment_method: 'transfer',
    payment_slip: null
  });
  const [paymentSlipPreview, setPaymentSlipPreview] = useState(null);
  const [banks, setBanks] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);

  useEffect(() => {
    fetchInvoices();
    fetchProducts();
    fetchCustomers();
    fetchBanks();
    fetchExchangeRates();
  }, []);
  
  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRates(response.data.exchange_rates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates');
    }
  };
  
  const getConvertedAmount = () => {
    if (!selectedInvoice || !paymentData.currency) return 0;
    
    const invoiceCurrency = selectedInvoice.currency || 'USD';
    
    // Same currency — no conversion needed
    if (paymentData.currency === invoiceCurrency) {
      return selectedInvoice.total;
    }
    
    // Convert invoice amount to USD first (if not already USD)
    let amountInUSD = selectedInvoice.total;
    if (invoiceCurrency !== 'USD') {
      const invoiceRate = exchangeRates.find(r => r.currency === invoiceCurrency);
      if (invoiceRate) {
        amountInUSD = selectedInvoice.total / invoiceRate.rate_to_usd;
      }
    }
    
    // Convert USD to payment currency
    if (paymentData.currency !== 'USD') {
      const paymentRate = exchangeRates.find(r => r.currency === paymentData.currency);
      if (paymentRate) {
        return amountInUSD * paymentRate.rate_to_usd;
      }
    }
    
    return amountInUSD;
  };
  
  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API}/settings/invoice-quotation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data.payment_banks || []);
      
      // Get unique currencies from all banks
      const currencies = new Set();
      response.data.payment_banks?.forEach(bank => {
        bank.accounts?.forEach(account => {
          if (account.account_number) {
            currencies.add(account.currency);
          }
        });
      });
      setAvailableCurrencies(Array.from(currencies));
    } catch (error) {
      console.error('Failed to fetch banks');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const selectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_name: customer.name,
      customer_email: customer.email || '',
      customer_phone: customer.phone || ''
    });
  };

  const searchProducts = (query) => {
    setProductSearch(query);
    if (!query.trim()) {
      setFilteredProducts(products);
      return;
    }
    const filtered = products.filter(p =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.sku.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const addProductToInvoice = (product) => {
    const existingItem = formData.items.find(item => item.product_id === product.id);
    
    if (existingItem) {
      const newItems = formData.items.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      );
      setFormData({ ...formData, items: newItems });
    } else {
      const available = product.quantity - (product.reserved_quantity || 0);
      if (available <= 0) {
        toast.error(`${product.name} is out of stock`);
        return;
      }
      
      setFormData({
        ...formData,
        items: [...formData.items, {
          product_id: product.id,
          product_name: product.name,
          description: product.name,
          quantity: 1,
          price: product.price,
          total: product.price
        }]
      });
    }
    setProductSearch('');
    setFilteredProducts(products);
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort invoices: unpaid/overdue first, then paid
      const sorted = response.data.sort((a, b) => {
        const statusPriority = { 'overdue': 1, 'unpaid': 2, 'paid': 3 };
        return (statusPriority[a.status] || 4) - (statusPriority[b.status] || 4);
      });
      
      setInvoices(sorted);
      setFilteredInvoices(sorted);
    } catch (error) {
      toast.error('Failed to load invoices');
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/status?status=paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: 'bg-emerald-100 text-emerald-700',
      unpaid: 'bg-orange-100 text-orange-700',
      overdue: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.unpaid;
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = (subtotal * formData.discount) / 100;
    const taxAmount = ((subtotal - discountAmount) * formData.tax) / 100;
    const total = subtotal - discountAmount + taxAmount;
    return { subtotal, discountAmount, taxAmount, total };
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { subtotal, discountAmount, taxAmount, total } = calculateTotals();

    try {
      await axios.post(`${API}/invoices`, {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        items: formData.items,
        subtotal,
        discount: discountAmount,
        tax: taxAmount,
        total,
        notes: formData.notes,
        due_date: formData.due_date || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Invoice created successfully');
      setShowDialog(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      items: [{ description: '', quantity: 1, price: 0, total: 0 }],
      discount: 0,
      tax: 10,
      notes: '',
      due_date: ''
    });
  };

  const startPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentStep(1);
    setPaymentData({
      currency: '',
      bank_id: '',
      payment_method: 'transfer',
      payment_slip: null
    });
    setPaymentSlipPreview(null);
    setShowPaymentDialog(true);
  };

  const handlePaymentSlipUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPaymentData({ ...paymentData, payment_slip: reader.result });
      setPaymentSlipPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const completePayment = async () => {
    try {
      // Get exchange rates to convert invoice amount to payment currency
      const currencyResponse = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const invoiceCurrency = selectedInvoice.currency || 'USD';
      let amountInPaymentCurrency = selectedInvoice.total;
      
      // Only convert if payment currency differs from invoice currency
      if (paymentData.currency !== invoiceCurrency) {
        const exchangeRates = currencyResponse.data.exchange_rates || [];
        
        // Convert invoice amount to USD first (if not already USD)
        let amountInUSD = selectedInvoice.total;
        if (invoiceCurrency !== 'USD') {
          const invoiceRate = exchangeRates.find(r => r.currency === invoiceCurrency);
          if (invoiceRate) {
            amountInUSD = selectedInvoice.total / invoiceRate.rate_to_usd;
          }
        }
        
        // Then convert USD to payment currency
        if (paymentData.currency !== 'USD') {
          const paymentRate = exchangeRates.find(r => r.currency === paymentData.currency);
          if (paymentRate) {
            amountInPaymentCurrency = amountInUSD * paymentRate.rate_to_usd;
          }
        } else {
          amountInPaymentCurrency = amountInUSD;
        }
      }
      
      // Record payment with converted amount
      await axios.post(`${API}/accounting/record-payment`, {
        reference_id: selectedInvoice.invoice_number,
        reference_type: 'invoice',
        payment_currency: paymentData.currency,
        payment_method: paymentData.payment_method,
        bank_id: paymentData.bank_id,
        amount: amountInPaymentCurrency,
        payment_slip: paymentData.payment_slip
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mark invoice as paid
      await axios.put(`${API}/invoices/${selectedInvoice.id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Payment recorded and invoice marked as paid!');
      setShowPaymentDialog(false);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to record payment: ' + (error.response?.data?.detail || error.message));
    }
  };

  const totals = calculateTotals();

  const handleExportPDF = () => {
    const rows = [];
    invoices.forEach((inv) => {
      (inv.items || []).forEach((item) => {
        rows.push([
          rows.length + 1,
          new Date(inv.created_at).toLocaleDateString(),
          inv.invoice_number || '-',
          item.product_name || item.description || '-',
          `$${fmtNum(item.price || 0)}`,
          item.quantity || 0,
          `$${fmtNum(item.total || 0)}`
        ]);
      });
    });
    const total = invoices.reduce((s, inv) => s + (inv.total || 0), 0);
    exportListPDF({
      title: 'Invoices',
      headers: ['#', 'Date', 'Invoice#', 'Item Name', 'Price', 'Qty', 'Total'],
      rows,
      totalLabel: 'Grand Total',
      totalValue: `$${fmtNum(total)}`,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="invoices-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
            <p className="text-slate-600 mt-1">{invoices.length} invoices total</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportPDF} data-testid="invoices-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-invoice-button">
                <Plus className="w-5 h-5 mr-2" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Invoice</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {customers.length > 0 && (
                  <div>
                    <Label>Select Existing Customer (Optional)</Label>
                    <Select onValueChange={(value) => {
                      const customer = customers.find(c => c.id === value);
                      if (customer) selectCustomer(customer);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose from saved customers..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} {customer.email ? `(${customer.email})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                      data-testid="invoice-customer-name"
                    />
                  </div>
                  <div>
                    <Label>Customer Email</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      data-testid="invoice-customer-email"
                    />
                  </div>
                  <div>
                    <Label>Customer Phone</Label>
                    <Input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Items</Label>
                    <Button type="button" size="sm" onClick={addItem} variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg">
                        <div className="col-span-5">
                          <Input
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                            required
                            min="1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price"
                            value={item.price}
                            onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value))}
                            required
                            min="0"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            value={`$${fmtNum(item.total)}`}
                            disabled
                          />
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeItem(index)}
                            disabled={formData.items.length === 1}
                            className="w-full h-10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Discount %</Label>
                    <Input
                      type="number"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <Label>Tax %</Label>
                    <Input
                      type="number"
                      value={formData.tax}
                      onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Internal Notes</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="For internal use only"
                    />
                  </div>
                </div>

                <div>
                  <Label>Special Notes for Customer</Label>
                  <textarea
                    className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md"
                    value={formData.customer_notes}
                    onChange={(e) => setFormData({ ...formData, customer_notes: e.target.value })}
                    placeholder="These notes will be visible to the customer on the invoice"
                  />
                </div>

                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-slate-700">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {formData.discount > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>Discount ({formData.discount}%):</span>
                      <span className="text-red-600">-{formatCurrency(totals.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-700">
                    <span>Tax ({formData.tax}%):</span>
                    <span>+{formatCurrency(totals.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total:</span>
                    <span>{formatCurrency(totals.total)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-invoice-button">
                    Create Invoice
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <SearchFilter
          data={invoices}
          onFilter={setFilteredInvoices}
          searchFields={['invoice_number', 'customer_name', 'customer_email']}
          placeholder="Search by invoice #, customer name..."
        />

        <div className="grid gap-4">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="p-6 hover:shadow-lg transition-all" data-testid={`invoice-${invoice.invoice_number}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Receipt className="w-5 h-5 text-slate-600" />
                    <h3 className="text-xl font-bold text-slate-900">{invoice.invoice_number}</h3>
                    <Badge className={getStatusColor(invoice.status)}>
                      {invoice.status}
                    </Badge>
                  </div>
                  <p className="text-slate-700 font-medium">{invoice.customer_name}</p>
                  {invoice.customer_email && (
                    <p className="text-sm text-slate-600">{invoice.customer_email}</p>
                  )}
                  <p className="text-sm text-slate-500 mt-2">
                    Created: {new Date(invoice.created_at).toLocaleDateString()}
                  </p>
                  {invoice.due_date && (
                    <p className="text-sm text-slate-500">
                      Due: {new Date(invoice.due_date).toLocaleDateString()}
                    </p>
                  )}
                  {invoice.payment_date && (
                    <p className="text-sm text-emerald-600">
                      Paid: {new Date(invoice.payment_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600">{formatCurrency(invoice.total, invoice.currency)}</p>
                  <div className="flex flex-col gap-2 mt-3">
                    <Button
                      onClick={() => {
                        setViewingInvoice(invoice);
                        setShowViewer(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid={`view-invoice-${invoice.invoice_number}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    {invoice.status === 'unpaid' && (
                      <>
                        <Button
                          onClick={() => {
                            // Populate form with invoice data for editing
                            setFormData({
                              customer_name: invoice.customer_name,
                              customer_email: invoice.customer_email || '',
                              customer_phone: invoice.customer_phone || '',
                              items: invoice.items,
                              discount: (invoice.discount / invoice.subtotal * 100) || 0,
                              tax: (invoice.tax / (invoice.subtotal - invoice.discount) * 100) || 10,
                              notes: invoice.notes || '',
                              due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : ''
                            });
                            setShowDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          <Receipt className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => startPayment(invoice)}
                          className="bg-emerald-600 hover:bg-emerald-700 w-full"
                          size="sm"
                          data-testid={`mark-paid-${invoice.invoice_number}`}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Mark as Paid
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Items:</p>
                <div className="space-y-1">
                  {invoice.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm text-slate-600">
                      <span>{item.description} (x{item.quantity})</span>
                      <span>{formatCurrency(item.total, invoice.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Invoice Viewer */}
        <InvoiceViewer
          invoice={viewingInvoice}
          open={showViewer}
          onClose={() => {
            setShowViewer(false);
            setViewingInvoice(null);
          }}
        />

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {paymentStep === 1 && 'Select Payment Currency'}
                {paymentStep === 2 && 'Select Bank to Credit'}
                {paymentStep === 3 && 'Upload Payment Slip'}
              </DialogTitle>
            </DialogHeader>

            {/* Step 1: Currency Selection */}
            {paymentStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Choose the currency customer paid in:</p>
                <div className="grid grid-cols-3 gap-3">
                  {availableCurrencies.map((currency) => (
                    <Button
                      key={currency}
                      variant={paymentData.currency === currency ? "default" : "outline"}
                      className={paymentData.currency === currency ? "bg-emerald-600" : ""}
                      onClick={() => {
                        setPaymentData({ ...paymentData, currency });
                        setPaymentStep(2);
                      }}
                    >
                      {currency}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Bank Selection */}
            {paymentStep === 2 && (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentStep(1)}
                  className="mb-2"
                >
                  ← Back
                </Button>
                <p className="text-sm text-slate-600">
                  Selected Currency: <strong>{paymentData.currency}</strong>
                </p>
                <p className="text-sm text-slate-600">Select bank to credit:</p>
                <div className="grid grid-cols-2 gap-3">
                  {banks.filter(bank => 
                    bank.accounts.some(acc => acc.currency === paymentData.currency && acc.account_number)
                  ).map((bank) => (
                    <Button
                      key={bank.id}
                      variant="outline"
                      className={`h-auto py-4 ${paymentData.bank_id === bank.id ? 'border-emerald-600 bg-emerald-50' : ''}`}
                      onClick={() => {
                        setPaymentData({ ...paymentData, bank_id: bank.id });
                        setPaymentStep(3);
                      }}
                    >
                      {bank.bank_logo && (
                        <img 
                          src={bank.bank_logo} 
                          alt={bank.bank_name} 
                          className="h-12 w-auto object-contain"
                        />
                      )}
                      {!bank.bank_logo && <span>{bank.bank_name}</span>}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Payment Slip Upload */}
            {paymentStep === 3 && (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentStep(2)}
                  className="mb-2"
                >
                  ← Back
                </Button>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>Currency: <strong>{paymentData.currency}</strong></p>
                  <p>Invoice Amount: <strong>{formatCurrency(selectedInvoice?.total, selectedInvoice?.currency)}</strong></p>
                  {paymentData.currency !== 'USD' && (
                    <p className="text-emerald-600 font-semibold">
                      Amount to Pay: <strong className="inline-flex items-center">
                        <CurrencySymbol currency={paymentData.currency} />
                        {fmtNum(getConvertedAmount())} {paymentData.currency}
                      </strong>
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>Upload Payment Slip (Optional)</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePaymentSlipUpload}
                    className="hidden"
                    id="payment-slip-upload"
                  />
                  <label htmlFor="payment-slip-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('payment-slip-upload').click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {paymentSlipPreview ? 'Change Slip' : 'Upload Slip'}
                    </Button>
                  </label>
                  {paymentSlipPreview && (
                    <div className="mt-2">
                      <img 
                        src={paymentSlipPreview} 
                        alt="Payment Slip" 
                        className="w-full h-32 object-cover rounded border"
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={completePayment}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete Payment & Mark Paid
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}